import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../config';

const PRESETS = [
  { label: 'This month', getRange: () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return { from, to: now.toISOString() };
  }},
  { label: 'Last 30 days', getRange: () => {
    const now = new Date();
    const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return { from, to: now.toISOString() };
  }},
  { label: 'Last 90 days', getRange: () => {
    const now = new Date();
    const from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
    return { from, to: now.toISOString() };
  }},
  { label: 'Year to date', getRange: () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), 0, 1).toISOString();
    return { from, to: now.toISOString() };
  }},
];

const ACTION_LABELS = {
  claude_research: 'Research (Claude)',
  claude_research_sweep: 'Intelligence sweep',
  claude_sequence: 'Sequence generation',
  exa_sweep: 'Daily signal sweep',
  exa_sweep_test: 'Signal sweep (test)',
  generate_icp: 'ICP generation',
  discovery_generate: 'Discovery brief',
  enrich_contact: 'Contact enrichment',
  find_or_create_company: 'Company lookup',
  find_companies: 'Find companies',
  find_contacts: 'Find contacts',
  icp_company_import: 'ICP company import',
  topup: 'Top-up',
  topup_manual_recovery: 'Manual top-up',
  monthly_reset: 'Monthly reset',
};

function formatGBP(n) { if (!n && n !== 0) return '£0'; return '£' + parseFloat(n).toFixed(2); }
function formatNum(n) { if (!n && n !== 0) return '0'; return parseInt(n).toLocaleString(); }
function formatDate(s) { if (!s) return '—'; return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }

const PAGE_SIZE = 50;

export default function AdminCosts() {
  const { getToken } = useAuth();
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState(PRESETS[0].getRange());
  const [presetIdx, setPresetIdx] = useState(0);

  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ actions: [], sources: [], clients: [] });

  // Transactions
  const [txRows, setTxRows] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [txFilters, setTxFilters] = useState({ client_id: '', action: '', source: '' });
  const [txLoading, setTxLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [llmData, setLlmData] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [llmRange, setLlmRange] = useState('30d');

  // Initial load: summary + clients + filter options
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/admin/costs/summary?from=${range.from}&to=${range.to}`).then(r => r.json()),
      fetch(`${API_BASE}/admin/costs/clients`).then(r => r.json()),
      fetch(`${API_BASE}/admin/costs/filter-options`).then(r => r.json()),
    ]).then(([s, c, f]) => {
      setSummary(s);
      setClients(Array.isArray(c) ? c : []);
      setFilterOptions(f || { actions: [], sources: [], clients: [] });
      setLoading(false);
    }).catch(e => { console.error(e); setLoading(false); });
  }, [range]);

  // Transactions: load when tab opens or filters/page change
  const loadTransactions = useCallback(() => {
    setTxLoading(true);
    const params = new URLSearchParams({
      limit: PAGE_SIZE, offset: txPage * PAGE_SIZE,
      from: range.from, to: range.to,
    });
    if (txFilters.client_id) params.append('client_id', txFilters.client_id);
    if (txFilters.action) params.append('action', txFilters.action);
    if (txFilters.source) params.append('source', txFilters.source);

    fetch(`${API_BASE}/admin/costs/transactions?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        setTxRows(data.rows || []);
        setTxTotal(data.total || 0);
        setTxLoading(false);
      })
      .catch(e => { console.error(e); setTxLoading(false); });
  }, [range, txPage, txFilters]);

  useEffect(() => {
    if (tab === 'transactions') loadTransactions();

    if (tab === 'llm') {
      (async () => {
        setLlmLoading(true);
        try {
          const token = await getToken();
          const r = await fetch(`${API_BASE}/admin/llm-spend?range=${llmRange}`, { headers: { 'Authorization': 'Bearer ' + token } });
          if (r.ok) setLlmData(await r.json());
        } catch (_) {} finally { setLlmLoading(false); }
      })();
    }
  }, [tab, llmRange, loadTransactions]);

  function exportCSV() {
    const params = new URLSearchParams({ format: 'csv', from: range.from, to: range.to });
    if (txFilters.client_id) params.append('client_id', txFilters.client_id);
    if (txFilters.action) params.append('action', txFilters.action);
    if (txFilters.source) params.append('source', txFilters.source);
    window.location.href = `${API_BASE}/admin/costs/transactions?${params.toString()}`;
  }

  function resetFilters() {
    setTxFilters({ client_id: '', action: '', source: '' });
    setTxPage(0);
  }

  if (loading || !summary) {
    return <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1.5rem' }}><p style={{ color: '#888' }}>Loading…</p></div>;
  }

  const totalSpent = summary.spend?.credits_spent || 0;
  const totalRevenue = summary.revenue?.gbp_revenue || 0;
  const totalCreditsAdded = summary.revenue?.credits_added || 0;
  const totalTopups = summary.revenue?.topups || 0;
  const transactionCount = summary.spend?.transactions || 0;

  const totalPages = Math.ceil(txTotal / PAGE_SIZE);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: '0 0 0.3rem', fontSize: '1.6rem', fontWeight: '700', color: '#1A1A1A' }}>Admin · Costs</h1>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#888' }}>Credit usage, revenue, and per-client breakdown.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {PRESETS.map((p, i) => (
            <button key={p.label} onClick={() => { setPresetIdx(i); setRange(p.getRange()); setTxPage(0); }}
              style={{
                fontSize: '0.78rem', padding: '0.4rem 0.75rem',
                background: presetIdx === i ? '#8B1A1A' : '#fff',
                color: presetIdx === i ? '#fff' : '#4A5568',
                border: '1px solid ' + (presetIdx === i ? '#8B1A1A' : '#D4CFC9'),
                borderRadius: '6px', cursor: 'pointer'
              }}>{p.label}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '0.5px solid #E8E4E0', marginBottom: '1.5rem' }}>
        {[{ key: 'overview', label: 'Overview' }, { key: 'clients', label: 'By client' }, { key: 'transactions', label: `Transactions${txTotal ? ' (' + txTotal.toLocaleString() + ')' : ''}` }, { key: 'llm', label: 'LLM spend' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '0.55rem 1rem', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid #8B1A1A' : '2px solid transparent', color: tab === t.key ? '#8B1A1A' : '#888', fontSize: '0.85rem', cursor: 'pointer', fontWeight: tab === t.key ? '500' : '400' }}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.85rem', marginBottom: '1.5rem' }}>
            <KPICard label="Revenue" value={formatGBP(totalRevenue)} sub={`${totalTopups} top-up${totalTopups === 1 ? '' : 's'}`} />
            <KPICard label="Credits added" value={formatNum(totalCreditsAdded)} sub="Paid + monthly resets" />
            <KPICard label="Credits spent" value={formatNum(totalSpent)} sub={`${transactionCount} action${transactionCount === 1 ? '' : 's'}`} />
            <KPICard label="Active clients" value={clients.filter(c => parseInt(c.lifetime_spent) > 0).length} sub={`of ${clients.length} total`} />
          </div>

          <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '600', color: '#1A1A1A' }}>Spend by action</h3>
            {summary.by_action.length === 0 ? <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>No spending in this period.</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Action</th><th style={{ ...th, textAlign: 'right' }}>Calls</th><th style={{ ...th, textAlign: 'right' }}>Credits</th><th style={{ ...th, textAlign: 'right' }}>Share</th></tr></thead>
                <tbody>
                  {summary.by_action.map((a, i) => {
                    const share = totalSpent > 0 ? (parseInt(a.credits) / parseInt(totalSpent) * 100).toFixed(1) : 0;
                    return (
                      <tr key={i} style={{ borderTop: '0.5px solid #F0EDE9' }}>
                        <td style={td}>{ACTION_LABELS[a.action] || a.action}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{formatNum(a.count)}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{formatNum(a.credits)}</td>
                        <td style={{ ...td, textAlign: 'right', color: '#888' }}>{share}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '600', color: '#1A1A1A' }}>Daily activity</h3>
            {summary.daily.length === 0 ? <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>No activity in this period.</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Day</th><th style={{ ...th, textAlign: 'right' }}>Credits spent</th><th style={{ ...th, textAlign: 'right' }}>Credits added</th><th style={{ ...th, textAlign: 'right' }}>Revenue</th></tr></thead>
                <tbody>
                  {summary.daily.slice().reverse().map((d, i) => (
                    <tr key={i} style={{ borderTop: '0.5px solid #F0EDE9' }}>
                      <td style={td}>{formatDate(d.day)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{formatNum(d.credits_spent)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{formatNum(d.credits_added)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{formatGBP(d.gbp_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === 'clients' && (
        <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#FAFAF8' }}>
              <tr><th style={th}>Client</th><th style={{ ...th, textAlign: 'right' }}>Balance</th><th style={{ ...th, textAlign: 'right' }}>Lifetime spent</th><th style={{ ...th, textAlign: 'right' }}>Lifetime revenue</th><th style={{ ...th, textAlign: 'right' }}>Last used</th></tr>
            </thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.id} style={{ borderTop: '0.5px solid #F0EDE9' }}>
                  <td style={td}>
                    <p style={{ margin: 0, fontWeight: '500', color: '#1A1A1A' }}>{c.name}</p>
                    <p style={{ margin: 0, fontSize: '0.72rem', color: c.status === 'active' ? '#276749' : '#888' }}>{c.status}</p>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{formatNum(parseInt(c.credits_remaining || 0) + parseInt(c.credits_topup || 0))}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: '600' }}>{formatNum(c.lifetime_spent)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#276749', fontWeight: '600' }}>{formatGBP(c.lifetime_revenue)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#888', fontSize: '0.8rem' }}>{formatDate(c.last_used_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'transactions' && (
        <>
          {/* Filter bar */}
          <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={txFilters.client_id} onChange={e => { setTxFilters(f => ({ ...f, client_id: e.target.value })); setTxPage(0); }}
              style={selectStyle}>
              <option value="">All clients</option>
              {filterOptions.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={txFilters.action} onChange={e => { setTxFilters(f => ({ ...f, action: e.target.value })); setTxPage(0); }}
              style={selectStyle}>
              <option value="">All actions</option>
              {filterOptions.actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
            </select>
            <select value={txFilters.source} onChange={e => { setTxFilters(f => ({ ...f, source: e.target.value })); setTxPage(0); }}
              style={selectStyle}>
              <option value="">All sources</option>
              {filterOptions.sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(txFilters.client_id || txFilters.action || txFilters.source) && (
              <button onClick={resetFilters} style={{ fontSize: '0.78rem', padding: '0.4rem 0.7rem', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', color: '#4A5568', cursor: 'pointer' }}>Clear filters</button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={exportCSV} style={{ fontSize: '0.78rem', padding: '0.4rem 0.85rem', background: '#1A1A1A', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}>Export CSV</button>
          </div>

          <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden', marginBottom: '0.85rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#FAFAF8' }}>
                <tr>
                  <th style={th}>Time</th><th style={th}>Client</th><th style={th}>Action</th><th style={th}>Source</th>
                  <th style={{ ...th, textAlign: 'right' }}>Δ</th><th style={{ ...th, textAlign: 'right' }}>Balance</th><th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {txLoading ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#888' }}>Loading…</td></tr>
                ) : txRows.length === 0 ? (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#888' }}>No transactions match these filters.</td></tr>
                ) : txRows.map(t => {
                  const stripeId = t.metadata?.stripe_session_id;
                  const stripeUrl = stripeId ? `https://dashboard.stripe.com/${stripeId.startsWith('cs_test_') ? 'test/' : ''}payments/${t.metadata?.payment_intent || stripeId}` : null;
                  return (
                    <tr key={t.id} style={{ borderTop: '0.5px solid #F0EDE9' }}>
                      <td style={{ ...td, fontSize: '0.78rem', color: '#888', whiteSpace: 'nowrap' }}>
                        {new Date(t.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={td}>{t.client_name || '—'}</td>
                      <td style={td}>{ACTION_LABELS[t.action] || t.action}</td>
                      <td style={{ ...td, fontSize: '0.78rem', color: '#888' }}>{t.source}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: '600', color: t.amount_delta < 0 ? '#C53030' : '#276749' }}>
                        {t.amount_delta > 0 ? '+' : ''}{formatNum(t.amount_delta)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: '#888' }}>{formatNum(t.balance_after)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {stripeUrl && (
                          <a href={stripeUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem', color: '#635BFF', textDecoration: 'none', fontWeight: '500' }}>Stripe →</a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', color: '#888' }}>
              <span>
                Showing {txPage * PAGE_SIZE + 1}–{Math.min((txPage + 1) * PAGE_SIZE, txTotal)} of {txTotal.toLocaleString()}
              </span>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={() => setTxPage(0)} disabled={txPage === 0} style={pageBtn(txPage === 0)}>« First</button>
                <button onClick={() => setTxPage(p => Math.max(0, p - 1))} disabled={txPage === 0} style={pageBtn(txPage === 0)}>‹ Prev</button>
                <span style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', color: '#1A1A1A' }}>Page {txPage + 1} of {totalPages}</span>
                <button onClick={() => setTxPage(p => p + 1)} disabled={txPage + 1 >= totalPages} style={pageBtn(txPage + 1 >= totalPages)}>Next ›</button>
                <button onClick={() => setTxPage(totalPages - 1)} disabled={txPage + 1 >= totalPages} style={pageBtn(txPage + 1 >= totalPages)}>Last »</button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'llm' && (
        <>
          <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem' }}>
            {[{ k: 'today', l: 'Today' }, { k: '7d', l: 'Last 7 days' }, { k: '30d', l: 'Last 30 days' }, { k: '90d', l: 'Last 90 days' }, { k: 'month', l: 'This month' }, { k: 'ytd', l: 'Year to date' }].map(r => (
              <button key={r.k} onClick={() => setLlmRange(r.k)}
                style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem', borderRadius: '20px', border: '0.5px solid ' + (llmRange === r.k ? '#8B1A1A' : '#D4CFC9'), color: llmRange === r.k ? '#8B1A1A' : '#888', background: llmRange === r.k ? '#FFF5F5' : '#FFF', cursor: 'pointer', fontWeight: llmRange === r.k ? '600' : '400' }}>{r.l}</button>
            ))}
          </div>

          {llmLoading && <p style={{ color: '#888', fontSize: '0.85rem' }}>Loading...</p>}
          {!llmLoading && llmData && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.85rem', marginBottom: '1.5rem' }}>
                <KPICard label="Total spend" value={formatGBP(llmData.totals.cost_gbp)} sub={`$${llmData.totals.cost_usd.toFixed(2)} USD`} />
                <KPICard label="Calls" value={formatNum(llmData.totals.calls)} sub={`${llmData.totals.failures} failed`} />
                <KPICard label="FX rate" value={`$1 = £${llmData.fx_usd_to_gbp.toFixed(2)}`} sub="USD to GBP" />
                <KPICard label="Providers" value={llmData.by_provider.length} sub="active" />
              </div>

              <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '600', color: '#1A1A1A' }}>Spend by provider</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead><tr style={{ borderBottom: '0.5px solid #E8E4E0' }}><th style={{ textAlign: 'left', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Provider</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Calls</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cost</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Share</th></tr></thead>
                  <tbody>
                    {llmData.by_provider.map(r => {
                      const share = llmData.totals.cost_usd > 0 ? (r.cost_usd / llmData.totals.cost_usd * 100).toFixed(1) : '0';
                      return (
                        <tr key={r.provider} style={{ borderBottom: '0.5px solid #F0EDE9' }}>
                          <td style={{ padding: '0.55rem 0', color: '#1A1A1A', textTransform: 'capitalize' }}>{r.provider}</td>
                          <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#555' }}>{formatNum(r.calls)}</td>
                          <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#1A1A1A', fontWeight: '500' }}>{formatGBP(r.cost_gbp)}</td>
                          <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#888' }}>{share}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '600', color: '#1A1A1A' }}>Spend by task</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead><tr style={{ borderBottom: '0.5px solid #E8E4E0' }}><th style={{ textAlign: 'left', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Task</th><th style={{ textAlign: 'left', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Model</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Calls</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Avg latency</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cost</th></tr></thead>
                  <tbody>
                    {llmData.by_task.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '0.5px solid #F0EDE9' }}>
                        <td style={{ padding: '0.55rem 0', color: '#1A1A1A' }}>{r.task}</td>
                        <td style={{ padding: '0.55rem 0', color: '#666', fontSize: '0.78rem' }}>{r.model}</td>
                        <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#555' }}>{formatNum(r.calls)}</td>
                        <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#888' }}>{r.avg_latency_ms ? r.avg_latency_ms + 'ms' : '—'}</td>
                        <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#1A1A1A', fontWeight: '500' }}>{formatGBP(r.cost_gbp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {llmData.by_client.length > 0 && (
                <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem', marginBottom: '1.25rem' }}>
                  <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '600', color: '#1A1A1A' }}>Spend by client (top 20)</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead><tr style={{ borderBottom: '0.5px solid #E8E4E0' }}><th style={{ textAlign: 'left', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Client</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Calls</th><th style={{ textAlign: 'right', padding: '0.5rem 0', color: '#888', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cost</th></tr></thead>
                    <tbody>
                      {llmData.by_client.map(r => (
                        <tr key={r.client_id} style={{ borderBottom: '0.5px solid #F0EDE9' }}>
                          <td style={{ padding: '0.55rem 0', color: '#1A1A1A' }}>{r.client_name || ('#' + r.client_id)}</td>
                          <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#555' }}>{formatNum(r.calls)}</td>
                          <td style={{ padding: '0.55rem 0', textAlign: 'right', color: '#1A1A1A', fontWeight: '500' }}>{formatGBP(r.cost_gbp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          {!llmLoading && !llmData && <p style={{ color: '#888', fontSize: '0.85rem' }}>No data yet. Trigger some sequence generation or research to see spend.</p>}
        </>
      )}
    </div>
  );
}

function KPICard({ label, value, sub }) {
  return (
    <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1rem 1.1rem' }}>
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '500' }}>{label}</p>
      <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: '#1A1A1A' }}>{value}</p>
      {sub && <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: '#888' }}>{sub}</p>}
    </div>
  );
}

const th = { textAlign: 'left', padding: '0.65rem 0.85rem', fontSize: '0.72rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '0.65rem 0.85rem', fontSize: '0.85rem', color: '#1A1A1A' };
const selectStyle = { fontSize: '0.82rem', padding: '0.4rem 0.6rem', border: '1px solid #D4CFC9', borderRadius: '6px', background: '#fff', color: '#1A1A1A', cursor: 'pointer', outline: 'none', fontFamily: 'inherit' };
const pageBtn = (disabled) => ({ fontSize: '0.78rem', padding: '0.3rem 0.7rem', background: disabled ? '#FAFAF8' : '#fff', color: disabled ? '#CCC' : '#4A5568', border: '1px solid ' + (disabled ? '#E8E4E0' : '#D4CFC9'), borderRadius: '6px', cursor: disabled ? 'default' : 'pointer' });
